import React, { useState } from 'react'
import mixpanel from 'mixpanel-browser';

import { Icon, Button, Grid } from 'semantic-ui-react'

import { Action, ActionType, draftToTrackData, DraftType, EmptyDraftData, Team, TournamentGameDetails } from '../types'

import { useMeleeContext } from '../context/MeleeContextProvider'
import { AddToHistory } from '../utils/roundHandler';

import { useNavigate } from 'react-router-dom'

import { Tournament } from 'tour/types'
import { PlayerNumberToColor } from 'draft/utils/colorUtils';

interface Props {
    CurrentAction?: Action,
    ActionSequence?: Action[],
    ApplyAction?: () => void,
    TournamentGameDetails?: TournamentGameDetails,
    draftType: DraftType
    TeamBlueTimer : string,
    TeamRedTimer : string,
}

export default function ActionMenu(props: Props) {
    const [loading, setLoading] = useState(false);

    const meleeContext = useMeleeContext();
    const navigate = useNavigate();


    let barColor = 'finished';
    let currentTeamsTurn = null;
    if (props.CurrentAction) {
        barColor = props.CurrentAction.Team.toLowerCase();
        if (props.draftType === DraftType.FFA && props.CurrentAction.PlayerNumber) {
            barColor = PlayerNumberToColor(props.CurrentAction.PlayerNumber);
        }

        currentTeamsTurn = props.CurrentAction.Team;
    }

    const backToMenu = (): void => {
        if(!meleeContext) return;
        mixpanel.track("Draft Completed",{
            "draftData" : draftToTrackData(meleeContext.gameData)
        })
        let disabledRound = Object.assign({}, meleeContext.gameData);
        disabledRound.Active = false;
        meleeContext?.setGameData(disabledRound);
    }

    const getIcon = () => {
        if (!props.CurrentAction) return null;

        switch (props.CurrentAction.Action) {
            case ActionType.Ban:
                return <Icon name='ban' />
            case ActionType.Pick:
                return <Icon name='hand point up outline' />
            case ActionType.Shop:
                return <Icon name='shop' />
            case ActionType.SelectEnemey:
                return <Icon name='crosshairs' />
            case ActionType.SelectFriendly:
                return <Icon name='hand lizard outline' />
        }
    }

    const skipShoppingTurn = () => {
        if (props.ApplyAction) {
            if (meleeContext!.gameData != null) {
                AddToHistory(meleeContext!.gameData)
            }
            props.ApplyAction();
        } else {
            console.error("Skip button is missing referance");
        }
    }

    const getText = () => {
        if (!meleeContext || !props.CurrentAction) return "";
        if (props.draftType === DraftType.FFA) {
            const player = meleeContext.gameData.Players.find(p => p.Number === props.CurrentAction?.PlayerNumber);
            return <h2>{getIcon()} {player?.Name}'s turn to {props.CurrentAction!.Action}</h2>;
        }

        if (ActionType.SelectFriendly === props.CurrentAction?.Action) {
            return <h2>{getIcon()} Select a teammate</h2>;
        }

        if (ActionType.SelectEnemey === props.CurrentAction?.Action) {
            return <h2>{getIcon()} Select an enemy</h2>;
        }

        // Default
        if (props.draftType === DraftType.Duel) {
            var duelistName = meleeContext.gameData.Players.find(p => p.Team === props.CurrentAction!.Team)?.Name;
            return <h2>{getIcon()} {duelistName}'s turn to {props.CurrentAction!.Action}</h2>;
        }
        return <h2>{getIcon()} Team {props.CurrentAction!.Team}'s turn to {props.CurrentAction!.Action}</h2>;
    }

    const saveAndGoBackToTournament = () => {
        if (!props.TournamentGameDetails) throw Error("Tournament Details missing");
        if (!meleeContext) throw new Error("Melee Context missing");

        setLoading(true);

        var tour = JSON.parse(String(localStorage.getItem(props.TournamentGameDetails.TournamentId))) as Tournament;
        let newTour = Object.assign({}, tour);

        // Store stuff on players   
        let currentGame = newTour.GroupStageGames.find(g => g.Id === props.TournamentGameDetails?.GameId);
        if (!currentGame) throw new Error("Can't find current game");

        currentGame.Stage = meleeContext.gameData.Stage;

        currentGame.TeamBlue.forEach(tp => {
            var pickedhero = meleeContext.gameData.Players.find(p => p.TournamentPlayerNumber === tp.Number)?.PickedHero;
            if (pickedhero) {
                tp.Hero = pickedhero;
            }
        })

        currentGame.TeamRed.forEach(tp => {
            var pickedhero = meleeContext.gameData.Players.find(p => p.TournamentPlayerNumber === tp.Number)?.PickedHero;
            if (pickedhero) {
                tp.Hero = pickedhero;
            }
        })

        meleeContext.setTournament(newTour)

        validateSaveAndNavigateBackToGame(newTour, currentGame!.Id, 10);
    }

    const validateSaveAndNavigateBackToGame = (newTour: Tournament, currentGameId: string, timeout: number) => {
        if (!meleeContext) throw new Error("Melee Context missing");

        // Validerer at vi har skrevet ferdig nye turneringen før vi går tilbake
        var idString = props.TournamentGameDetails!.TournamentId.split('#')[1];
        var stored = String(localStorage.getItem('Tournament#' + idString));

        if (stored !== JSON.stringify(newTour)) {
            if (timeout > 5000) {
                setLoading(false);
                throw new Error("Something went wrong - cant store draft")
            }

            setTimeout(() => {
                validateSaveAndNavigateBackToGame(newTour, currentGameId, timeout *= 2);
            }, timeout);
            return;
        }

        mixpanel.track("Tournament Draft Completed",{
            "draftData" : draftToTrackData(meleeContext?.gameData)
        })

        let newDraft = Object.assign({}, meleeContext.gameData);
        newDraft.Active = false;
        meleeContext && meleeContext.setGameData(EmptyDraftData());

        navigate(`/tournament/${idString}?open=${currentGameId}`);
    }


    return (
        <div className={'action-menu-container ' + barColor}>
            <Grid columns={3} divided>
                {/* Blue */}
                <Grid.Column width={4} className={`action-menu-timer-box ${currentTeamsTurn === Team.Blue ? '' : 'grayed'}`}>
                    <h3>{props.TeamBlueTimer}</h3>
                </Grid.Column>
                {/* Action */}
                <Grid.Column width={8}>
                {props.CurrentAction ?
                <div>
                    {getText()}
                    {props.CurrentAction.Action === ActionType.Shop && <Button onClick={skipShoppingTurn} size='big' inverted>Skip</Button>}
                </div>
                : (
                    <>
                        {props.TournamentGameDetails ? (
                            <div>
                                <Button
                                    icon
                                    labelPosition='left'
                                    inverted
                                    color='yellow'
                                    size='big'
                                    loading={loading}
                                    onClick={saveAndGoBackToTournament}
                                >
                                    <Icon name='save' />
                                    Save and go back
                                </Button>
                            </div>
                        ) : (
                            <div>
                                <h2>Let's Go!</h2>
                                <Button inverted color='green' onClick={backToMenu}>Done</Button>
                            </div>
                        )
                        }
                    </>
                )}
                </Grid.Column>
                {/* Red */}
                <Grid.Column width={4} className={`action-menu-timer-box ${currentTeamsTurn === Team.Red ? '' : 'grayed'}`}>
                    <h3>{props.TeamRedTimer}</h3>
                </Grid.Column>
            </Grid>
        </div>
    )
}
